if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();

      const template = this.querySelector('template');
      this.errorHtml = template?.content.firstElementChild?.cloneNode(true);
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.abortController = null;
      this.requestId = 0;
      this.responseCache = new Map();
      if(!this.hasAttribute('available')) return;

      this.fetchAvailability(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.requestId += 1;
      this.abortController?.abort();
      this.abortController = null;
    }

    update(variant) {
      const variantId = typeof variant === 'object' ? variant?.id : variant;
      if (!variantId || variant?.available === false) {
        this.dataset.variantId = '';
        this.innerHTML = '';
        this.removeAttribute('available');
        return;
      }

      const normalizedVariantId = String(variantId);
      if (this.dataset.variantId === normalizedVariantId && this.hasAttribute('available')) return;

      this.dataset.variantId = normalizedVariantId;
      this.fetchAvailability(normalizedVariantId);
    }

    fetchAvailability(variantId, force = false) {
      if (!variantId) return;

      const normalizedVariantId = String(variantId);
      const currentRequestId = ++this.requestId;
      this.abortController?.abort();

      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith("/")) {
        rootUrl = rootUrl + "/";
      }
      const variantSectionUrl = `${rootUrl}variants/${normalizedVariantId}/?section_id=pickup-availability`;
      const cachedEntry = force ? null : this.responseCache.get(normalizedVariantId);
      const cachedResponse = cachedEntry && cachedEntry.expiresAt > Date.now() ? cachedEntry.text : null;
      if (!cachedResponse && cachedEntry) this.responseCache.delete(normalizedVariantId);
      const controller = new AbortController();
      this.abortController = controller;

      const responsePromise = cachedResponse
        ? Promise.resolve(cachedResponse)
        : fetch(variantSectionUrl, { signal: controller.signal })
            .then(response => {
              if (!response.ok) throw new Error(`Pickup availability request failed: ${response.status}`);
              return response.text();
            })
            .then(text => {
              this.responseCache.set(normalizedVariantId, {
                text,
                expiresAt: Date.now() + 15000,
              });
              return text;
            });

      responsePromise
        .then(text => {
          if (controller.signal.aborted || currentRequestId !== this.requestId
            || this.dataset.variantId !== normalizedVariantId || !this.isConnected) return;

          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.shopify-section');
          this.renderPreview(sectionInnerHTML);
        })
        .catch(e => {
          if (controller.signal.aborted || currentRequestId !== this.requestId) return;
          this.responseCache.delete(normalizedVariantId);
          const button = this.querySelector('button');
          if (button) button.removeEventListener('click', this.onClickRefreshList);
          this.renderError();
        })
        .finally(() => {
          if (currentRequestId === this.requestId) this.abortController = null;
        });
    }

    onClickRefreshList(evt) {
      this.fetchAvailability(this.dataset.variantId, true);
    }

    renderError() {
      this.innerHTML = '';
      if (!this.errorHtml) return;
      this.appendChild(this.errorHtml);

      this.querySelector('button').addEventListener('click', this.onClickRefreshList);
    }

    renderPreview(sectionInnerHTML) {
      const drawer = document.querySelector('pickup-availability-drawer');
      if (drawer) drawer.remove();
      if (!sectionInnerHTML?.querySelector?.('pickup-availability-preview')) {
        this.innerHTML = "";
        this.removeAttribute('available');
        return;
      }

      this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
      this.setAttribute('available', '');

      document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));

      const button = this.querySelector('button');
      if (button) button.addEventListener('click', (evt) => {
        document.querySelector('pickup-availability-drawer').show(evt.target);
      });
    }
  });
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer', class PickupAvailabilityDrawer extends HTMLElement {
    constructor() {
      super();

      this.onBodyClick = this.handleBodyClick.bind(this);

      this.querySelector('button').addEventListener('click', () => {
        this.hide();
      });

      this.addEventListener('keyup', (event) => {
        if(event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
    }

    handleBodyClick(evt) {
      const target = evt.target;
      if (target != this && !target.closest('pickup-availability-drawer') && target.id != 'ShowPickupAvailabilityDrawer') {
        this.hide();
      }
    }

    hide() {
      this.removeAttribute('open');
      document.body.removeEventListener('click', this.onBodyClick);
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.focusElement);
    }

    show(focusElement) {
      this.focusElement = focusElement;
      this.setAttribute('open', '');
      document.body.addEventListener('click', this.onBodyClick);
      document.body.classList.add('overflow-hidden');
      trapFocus(this);
    }
  });
}
